// ============================================================================
// DUAL PURCHASE INTERFACE - PRODUCT PAGE
// ============================================================================
// Provides subscription vs one-time purchase selection interface on product page
// 
// Features:
// - Toggle between "Subscribe & Save" and "One-time Purchase" modes
// - Dynamic image swapping based on:
//   * Subscription plan selection (FREQUENT/REGULAR/LOW)
//   * OTP pack size selection (42-pack/21-pack)
// - Recharge widget dimming when OTP selected
// - Click Recharge widget to activate subscription mode
// - Smart cart handling (clear + add with correct selling plan)
// - Conditional redirect (subscription → cart drawer, OTP → checkout)
// - Back button state restoration
// ============================================================================

(function() {
  
  // ============================================================================
  // GLOBAL STATE
  // ============================================================================
  let currentMode = 'subscription'; // Current purchase mode: 'subscription' or 'onetime'
  let otpVariantId = null; // Currently selected OTP variant ID
  let otpQuantity = 1; // Currently selected OTP quantity (1-7)
  let config = {}; // Product configuration from metafield
  
  // ============================================================================
  // RECHARGE WIDGET CONTROL
  // ============================================================================
  
  /**
   * Gets the Recharge widget element from DOM
   * Utility function to avoid repeating selector logic
   * @returns {Element|null} Recharge widget element or null
   */
  function getRechargeWidget() {
    return document.querySelector('recharge-subscription-widget') ||
           document.querySelector('[data-recharge-widget]') ||
           document.querySelector('.shopify-app-block');
  }
  
  /**
   * Dims or restores the Recharge subscription widget
   * Used to visually indicate when OTP mode is active
   * @param {boolean} shouldDim - True to dim widget, false to restore
   */
  function dimRechargeWidget(shouldDim) {
    const rechargeContainer = getRechargeWidget();
    
    if (rechargeContainer) {
      if (shouldDim) {
        rechargeContainer.style.opacity = '0.3';
        rechargeContainer.style.pointerEvents = 'none';
        rechargeContainer.style.transition = 'opacity 0.3s ease';
      } else {
        rechargeContainer.style.opacity = '1';
        rechargeContainer.style.pointerEvents = 'auto';
      }
    }
  }
  
  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================
  
  /**
   * Replaces placeholders in message template with plan data
   * @param {string} message - Message template with {plan}, {packs}, {price}, {delivery} placeholders
   * @param {Object} plan - Plan object with title, packsPerDay, pricePerDay, deliveryFrequency
   * @returns {string} Message with placeholders replaced
   */
  function replacePlaceholders(message, plan) {
    if (!message || !plan) return message || '';
    return message
      .replace('{plan}', plan.title || '')
      .replace('{packs}', plan.packsPerDay || '')
      .replace('{price}', plan.pricePerDay || '')
      .replace('{delivery}', plan.deliveryFrequency ? 'every ' + plan.deliveryFrequency + ' weeks' : '');
  }
  
  // ============================================================================
  // MAIN INITIALIZATION
  // ============================================================================
  
  /**
   * Loads configuration and initializes dual purchase interface
   * Uses config from product metafield (loaded via subscription-config.liquid)
   */
  function loadConfigAndInit() {
    // Use config from metafield (already loaded in window.SUBSCRIPTION_CONFIG)
    config = window.SUBSCRIPTION_CONFIG || {
      planImages: {},
      defaultPlanId: '',
      otpProductHandle: '',
      variants: {},
      buttonText: {
        subscription: 'Add to Cart',
        otp: 'Add to Cart'
      },
      lineItemProperties: {
        orderTypeSub: '',
        orderTypeOtp: '',
        trialFirst: '',
        trialWithSub: ''
      }
    };

    if (window.JD_DEBUG) {
      const cfg = config;
      const plans = cfg.plans || [];
      const variants = cfg.variants || {};
      window.JD_DEBUG.log('=== SUBSCRIPTION_CONFIG diagnostic ===');
      window.JD_DEBUG.log('Source:', window.SUBSCRIPTION_CONFIG ? 'window.SUBSCRIPTION_CONFIG' : 'FALLBACK (config not loaded)');
      window.JD_DEBUG.log('defaultPlanId:', cfg.defaultPlanId, '| type:', typeof cfg.defaultPlanId);
      window.JD_DEBUG.log('variants.defaultSubPack:', variants.defaultSubPack, '| type:', typeof variants.defaultSubPack);
      window.JD_DEBUG.log('variants.subTrialPack:', variants.subTrialPack, '| type:', typeof variants.subTrialPack);
      window.JD_DEBUG.log('variants.otpTrialPack:', variants.otpTrialPack, '| type:', typeof variants.otpTrialPack);
      window.JD_DEBUG.log('plans count:', plans.length);
      plans.forEach(function(p, i) {
        window.JD_DEBUG.log('  plan[' + i + '] id:', p.id, '| subPlanId:', p.subPlanId, '(', typeof p.subPlanId, ') | trialPlanId:', p.trialPlanId, '(', typeof p.trialPlanId, ')');
      });
      window.JD_DEBUG.log('planImages keys:', Object.keys(cfg.planImages || {}).length);
      window.JD_DEBUG.log('lineItemProperties:', cfg.lineItemProperties);
      if (!window.SUBSCRIPTION_CONFIG) window.JD_DEBUG.warn('window.SUBSCRIPTION_CONFIG missing — check jd-subscription-config.liquid loaded and metaobject is configured');
      if (!variants.defaultSubPack) window.JD_DEBUG.warn('variants.defaultSubPack missing');
      if (!plans.length) window.JD_DEBUG.warn('plans array empty — plan image switching and frequency mapping will not work');
      window.JD_DEBUG.log('=====================================');
    }

    initDualPurchase(config);
  }

  /**
   * Initializes the dual purchase interface with loaded config
   * @param {Object} config - Product configuration from JSON file
   */
  function initDualPurchase(config) {
    // ============================================================================
    // DOM ELEMENT REFERENCES
    // ============================================================================
    const subscriptionRadio = document.getElementById('purchase-subscription');
    const onetimeRadio = document.getElementById('purchase-onetime');
    const subscriptionSection = document.querySelector('.subscription-section');
    const onetimeSection = document.querySelector('.onetime-section');
    const otpPackInputs = document.querySelectorAll('input[name="otp-pack"]');

    // If DOM elements not ready, retry after delay
    if (!subscriptionRadio || !onetimeRadio) {
      setTimeout(function() { loadConfigAndInit(); }, 100);
      return;
    }
    
    // ============================================================================
    // CACHED DOM SELECTORS
    // ============================================================================
    // Cache frequently accessed DOM elements for better performance
    const mainImageSelector = '[class*="media-FeatureMedia"] img.media__styled-element, .product__media img, .product__modal-opener img, li.feature-media-item img, .image-magnify-lightbox';
    let cachedMainImage = document.querySelector(mainImageSelector);
    
    /**
     * Gets the main product image element (with cache)
     * @returns {Element|null} Main image element
     */
    function getMainImage() {
      if (!cachedMainImage || !document.contains(cachedMainImage)) {
        cachedMainImage = document.querySelector(mainImageSelector);
      }
      return cachedMainImage;
    }
    
    // ============================================================================
    // CONFIGURATION VALUES
    // ============================================================================
    const planImageMap = config.planImages || {}; // Maps plan IDs to image URLs

    const defaultPlanId = config.defaultPlanId || '692153319762'; // Default plan (FREQUENT)
    const buttonText = config.buttonText || { subscription: 'Free Trial + Check Out', otp: 'Check Out' };

    // Show delivery frequency for default plan on load
    (function() {
      const plans = config.plans || [];
      const defaultPlan = plans.find(function(p) {
        return String(p.subPlanId) === String(defaultPlanId) || String(p.trialPlanId) === String(defaultPlanId);
      });
      const el = document.querySelector('.plan-delivery-info');
      if (el && defaultPlan && defaultPlan.deliveryFrequency) {
        el.textContent = buildDeliveryInfo(defaultPlan.deliveryFrequency, defaultPlan.pricePerDay);
        el.hidden = false;
      }
    }());
    
    // Cache the default subscription image URL on page load
    // This ensures we can restore it when switching back to subscription mode
    let subscriptionImageUrl = null;
    const initialImage = document.querySelector('.product__media img') ||
                        document.querySelector('.product__modal-opener img') ||
                        document.querySelector('.image-magnify-lightbox');
    if (initialImage && initialImage.src) {
      subscriptionImageUrl = initialImage.src;
    }
    
    // ============================================================================
    // PLAN SELECTION TRACKING
    // ============================================================================
    // Tracks which subscription plan is selected and whether user has changed from default
    let lastPlanId = defaultPlanId; // Last known plan ID
    let planCheckInterval = null; // Interval for polling plan changes
    let hasChangedPlan = false; // Whether user has changed from default plan
    let inactivityTimeout = null; // Timeout to stop polling after inactivity (LOW-2 optimization)
    
    // ============================================================================
    // UI UPDATE FUNCTIONS
    // ============================================================================
    
    /**
     * Updates the buy button text based on current mode
     * Subscription: "Free Trial + Check Out"
     * OTP: "Check Out"
     */
    function updateBuyButtonText() {
      if ((window.JD_STORE_FLAGS || {}).soldOutMode) return;
      const rawText = currentMode === 'subscription' ? buttonText.subscription : buttonText.otp;
      if (!rawText) return;
      let newText = rawText;
      if (currentMode === 'subscription') {
        const plans = config.plans || [];
        const plan = plans.find(function(p) {
          return String(p.subPlanId) === String(lastPlanId) || String(p.trialPlanId) === String(lastPlanId);
        });
        if (plan) newText = replacePlaceholders(rawText, plan);
      }

      // Override the source so Alpine reads our value if it re-renders
      if (window.theme && window.theme.strings) {
        window.theme.strings.addToCart = newText;
      }

      // Also set directly for immediate effect
      const textEl = document.querySelector('[x-text="addToCartText"]');
      if (textEl) {
        textEl.textContent = newText;
      } else {
        setTimeout(updateBuyButtonText, 500);
      }
    }
    
    /**
     * Updates the main product image based on current selection
     * For subscription mode: shows cached subscription image or plan-specific image
     * For OTP mode: fetches and shows variant-specific image
     */
    function updateProductImage() {
      const mainImage = getMainImage();
      
      if (!mainImage) return;
      
      // SUBSCRIPTION MODE: Show cached subscription image
      if (currentMode === 'subscription') {
        if (subscriptionImageUrl) {
          mainImage.src = subscriptionImageUrl;
          mainImage.srcset = subscriptionImageUrl;
          mainImage.setAttribute('src', subscriptionImageUrl);
        }
      }
      // OTP MODE: Show variant-specific image using preloaded data
      else if (currentMode === 'onetime' && otpVariantId) {
        // Use preloaded OTP product data from config (no fetch needed!)
        const otpProduct = config.otpProduct;
        if (otpProduct && otpProduct.variants) {
          const variant = otpProduct.variants.find(function(v) {
            return v.id == otpVariantId;
          });
          
          if (variant && variant.image) {
            // Navigate to slide 1 so the feature image is visible
            const firstThumbnail = document.querySelector('li.product-thumbnail-list-item[data-media-id] a');
            if (firstThumbnail) {
              firstThumbnail.addEventListener('click', function(e) { e.preventDefault(); }, { capture: true, once: true });
              firstThumbnail.click();
            }

            setTimeout(function() {
              const featureImg = getMainImage();
              if (featureImg) {
                featureImg.src = variant.image;
                featureImg.srcset = variant.image;
                featureImg.setAttribute('src', variant.image);
              }
            }, 50);
          }
        }
      }
    }

    /**
     * Updates the product image to show plan-specific image
     * Each subscription plan (FREQUENT/REGULAR/LOW) has a unique image
     * @param {string} planId - Selling plan ID
     */
    function updateImageForPlan(planId) {
      const imageUrl = planImageMap[planId];
      if (!imageUrl) return;

      // Update subscriptionImageUrl so switching back to sub mode shows the correct plan image
      subscriptionImageUrl = imageUrl;

      // Directly update main image (same approach as OTP mode — reliable across devices)
      const mainImage = getMainImage();
      if (mainImage) {
        mainImage.src = imageUrl;
        mainImage.srcset = imageUrl;
        mainImage.setAttribute('src', imageUrl);
      }

      // Also attempt thumbnail click for gallery sync (desktop)
      const urlFilename = imageUrl.split('?')[0].split('/').pop();
      let targetMediaId = null;
      document.querySelectorAll('li.feature-media-item[data-media-id]').forEach(function(slide) {
        const img = slide.querySelector('img');
        if (img && img.src.split('?')[0].split('/').pop() === urlFilename) {
          targetMediaId = slide.dataset.mediaId;
        }
      });

      if (targetMediaId) {
        // Navigate Splide carousel to the matching slide (mobile only — destroyed on desktop)
        const splideEl = document.querySelector('.splide--product');
        const splide = splideEl && window.slideshows && window.slideshows[splideEl.id];
        const splideDestroyed = splide && window.Splide && splide.state.is(window.Splide.STATES.DESTROYED);
        if (splide && !splideDestroyed) {
          const slides = document.querySelectorAll('.splide--product li.feature-media-item[data-media-id]');
          let slideIndex = -1;
          slides.forEach(function(sl, i) {
            if (sl.dataset.mediaId === targetMediaId) slideIndex = i;
          });
          if (slideIndex >= 0) {
            splide.go(slideIndex);
            return;
          }
        }

        // Fallback: thumbnail click (desktop) or scrollIntoView (mobile)
        const thumbnail = document.querySelector(
          'li.product-thumbnail-list-item[data-media-id="' + targetMediaId + '"] a'
        );
        if (thumbnail) {
          thumbnail.addEventListener('click', function(e) { e.preventDefault(); }, { capture: true, once: true });
          thumbnail.click();
        } else {
          const slide = document.querySelector('li.feature-media-item[data-media-id="' + targetMediaId + '"]');
          if (slide) slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        }
      }
    }

    // ============================================================================
    // PLAN SELECTION MONITORING
    // ============================================================================

    /**
     * Builds the delivery info string using metaobject template or fallback.
     * Template field: delivery_info_template (e.g. "Delivered every {delivery} weeks")
     */
    function buildDeliveryInfo(frequency, price) {
      const template = (config.deliveryInfoTemplate || '').trim();
      if (template) {
        return template.replace('{delivery}', frequency).replace('{price}', price || '');
      }
      return 'Delivered every ' + frequency + ' weeks';
    }

    /**
     * Updates the .plan-delivery-info element with the delivery frequency for the active plan
     */
    function updateDeliveryInfo(planId) {
      const plans = config.plans || [];
      const plan = plans.find(function(p) {
        return String(p.subPlanId) === String(planId) || String(p.trialPlanId) === String(planId);
      });
      const el = document.querySelector('.plan-delivery-info');
      if (!el) return;
      if (currentMode === 'onetime') {
        el.hidden = true;
        return;
      }
      if (plan && plan.deliveryFrequency) {
        el.textContent = buildDeliveryInfo(plan.deliveryFrequency, plan.pricePerDay);
        el.hidden = false;
      } else {
        el.hidden = true;
      }
    }

    /**
     * Checks if user has changed subscription plan and updates image accordingly
     * Cannot directly access Recharge shadow DOM, so polls the selling_plan input
     * Stops polling after 5 seconds of inactivity (LOW-2 optimization)
     */
    function checkPlanSelection() {
      if (currentMode !== 'subscription') return;

      const sellingPlanInput = document.querySelector('input[name="selling_plan"]');
      if (sellingPlanInput && sellingPlanInput.value !== lastPlanId) {
        hasChangedPlan = true;
        lastPlanId = sellingPlanInput.value;
        updateImageForPlan(lastPlanId);
        updateDeliveryInfo(lastPlanId);
        updateBuyButtonText();
        
        // Reset inactivity timeout on plan change
        if (inactivityTimeout) clearTimeout(inactivityTimeout);
        inactivityTimeout = setTimeout(function() {
          stopPlanWatcher();
        }, 5000); // Stop polling after 5 seconds of no changes
      }
    }

    /**
     * Starts polling for plan selection changes
     * Only polls if user has changed from default plan (performance optimization)
     * Resets inactivity timeout to auto-stop after 5 seconds
     */
    function startPlanWatcher() {
      if (planCheckInterval) clearInterval(planCheckInterval);
      planCheckInterval = setInterval(checkPlanSelection, 500);
      checkPlanSelection();
      
      // Set inactivity timeout when starting watcher
      if (inactivityTimeout) clearTimeout(inactivityTimeout);
      inactivityTimeout = setTimeout(function() {
        stopPlanWatcher();
      }, 5000); // Stop polling after 5 seconds of no changes
    }

    /**
     * Stops polling for plan selection changes
     * Called when switching to OTP mode or after inactivity
     */
    function stopPlanWatcher() {
      if (planCheckInterval) {
        clearInterval(planCheckInterval);
        planCheckInterval = null;
      }
      if (inactivityTimeout) {
        clearTimeout(inactivityTimeout);
        inactivityTimeout = null;
      }
    }

    // Listen for clicks on Recharge container to detect plan changes
    const rechargeContainerForPlan = getRechargeWidget();
    if (rechargeContainerForPlan) {
      rechargeContainerForPlan.addEventListener('click', function() {
        if (currentMode === 'subscription') {
          setTimeout(function() {
            checkPlanSelection();
            if (hasChangedPlan) {
              startPlanWatcher();
            }
          }, 200);
        }
      });
    }
    
    // ============================================================================
    // MODE SWITCHING
    // ============================================================================
    
    /**
     * Updates UI when switching between subscription and OTP modes
     * Handles section visibility, widget dimming, button text, and images
     */
    function updateSections() {
      if (subscriptionRadio.checked) {
        // SUBSCRIPTION MODE
        currentMode = 'subscription';
        subscriptionSection.classList.add('active');
        onetimeSection.classList.remove('active');
        document.body.classList.remove('otp-mode');
        document.body.classList.add('subscription-mode');
        
        dimRechargeWidget(false); // Restore Recharge widget
        updateBuyButtonText();

        // Show delivery info in subscription mode
        const deliveryInfoEl = document.querySelector('.plan-delivery-info');
        if (deliveryInfoEl) deliveryInfoEl.hidden = false;
        
        // Reset OTP quantity selectors to 1
        const qtySelectors = document.querySelectorAll('.otp-qty-selector');
        qtySelectors.forEach(function(selector) {
          selector.value = '1';
        });
        otpQuantity = 1;
        
        // Restore plan-specific image if user changed plans, otherwise default
        if (hasChangedPlan && lastPlanId) {
          updateImageForPlan(lastPlanId);
        } else {
          updateProductImage(); 
        }
        
        startPlanWatcher(); // Start monitoring plan changes

        // Scroll to top of product section on mobile
        if (window.innerWidth < 750) {
          const productSection = document.querySelector('.product__media, .splide--product, [data-product-root]');
          if (productSection) {
            setTimeout(function() {
              productSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 50);
          }
        }

      } else {
        // OTP MODE
        currentMode = 'onetime';
        subscriptionSection.classList.remove('active');
        onetimeSection.classList.add('active');
        document.body.classList.add('otp-mode');
        document.body.classList.remove('subscription-mode');

        dimRechargeWidget(true); // Dim Recharge widget
        updateBuyButtonText();

        // Hide delivery info in OTP mode
        const deliveryInfoEl = document.querySelector('.plan-delivery-info');
        if (deliveryInfoEl) deliveryInfoEl.hidden = true;
        updateProductImage(); // Show OTP variant image
        stopPlanWatcher(); // Stop monitoring plan changes

        // Store selected OTP pack
        const selectedPack = document.querySelector('input[name="otp-pack"]:checked');
        if (selectedPack) {
          otpVariantId = selectedPack.dataset.variantId;
        }

        // Scroll checkout button into view on mobile
        if (window.innerWidth < 750) {
          const buyBtn = document.querySelector('.product-block-buy-buttons');
          if (buyBtn) {
            setTimeout(function() {
              buyBtn.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }, 50);
          }
        }
      }
    }
    
    // Add event listeners to radio buttons
    subscriptionRadio.addEventListener('change', updateSections);
    onetimeRadio.addEventListener('change', updateSections);
    
    // ============================================================================
    // OTP PACK SELECTION
    // ============================================================================
    
    /**
     * Handle OTP pack selection (42-pack vs 21-pack)
     * Updates stored variant ID and switches image
     */
    otpPackInputs.forEach(function(input) {
      input.addEventListener('change', function(e) {
        if (currentMode === 'onetime') {
          otpVariantId = this.dataset.variantId;
          
          // Reset ALL quantity selectors to 1 when switching packs
          const allQtySelectors = document.querySelectorAll('.otp-qty-selector');
          allQtySelectors.forEach(function(selector) {
            selector.value = '1';
          });
          
          // Set quantity to 1
          otpQuantity = 1;
          
          updateProductImage();
        }
      });
    });
    
    /**
     * Handle quantity selector changes for OTP packs
     * Auto-selects the pack when quantity is changed
     */
    const qtySelectors = document.querySelectorAll('.otp-qty-selector');
    qtySelectors.forEach(function(selector) {
      selector.addEventListener('change', function() {
        const packOption = this.closest('.otp-pack-option');
        const packRadio = packOption.querySelector('input[type="radio"]');
        
        // Update quantity
        const newQuantity = parseInt(this.value);
        
        // If this pack is selected, just update quantity
        if (packRadio && packRadio.checked) {
          otpQuantity = newQuantity;
        } else {
          // Auto-select this pack when changing its quantity
          packRadio.checked = true;
          otpVariantId = packRadio.dataset.variantId;
          otpQuantity = newQuantity;
          
          // Switch to OTP mode if not already
          if (currentMode !== 'onetime') {
            const onetimeRadio = document.getElementById('purchase-onetime');
            if (onetimeRadio) {
              onetimeRadio.checked = true;
              updateSections();
            }
          }
        }
      });
    });
    
    // ============================================================================
    // RECHARGE WIDGET CLICK HANDLING
    // ============================================================================
    
    /**
     * Watches for clicks on Recharge widget to activate subscription mode
     * Uses capture phase to catch clicks before Recharge processes them
     */
    function watchRechargeClicks() {
      const rechargeContainer = getRechargeWidget();
      
      if (rechargeContainer) {
        rechargeContainer.addEventListener('click', function() {
          if (currentMode !== 'subscription') {
            subscriptionRadio.checked = true;
            updateSections();
          }
        }, true); // Use capture phase
      } else {
        // Recharge not ready yet, retry
        setTimeout(watchRechargeClicks, 500);
      }
    }
    
    // Global click listener as backup
    document.addEventListener('click', function(e) {
      const target = e.target;
      const rechargeElement = target.closest('recharge-subscription-widget') ||
                             target.closest('[data-recharge-widget]') ||
                             target.closest('.shopify-app-block');
      
      if (rechargeElement && currentMode !== 'subscription') {
        subscriptionRadio.checked = true;
        updateSections();
      }
    }, true);
    
    // ============================================================================
    // INITIALIZATION
    // ============================================================================
    
    updateSections(); // Set initial mode
    updateBuyButtonText(); // Set initial button text
    updateProductImage(); // Set initial image
    
    // Store initial OTP selection if any
    const initialOTP = document.querySelector('input[name="otp-pack"]:checked');
    if (initialOTP) {
      otpVariantId = initialOTP.dataset.variantId;
      
      // Get initial quantity from dropdown
      const initialQtySelector = initialOTP.closest('.otp-pack-option').querySelector('.otp-qty-selector');
      otpQuantity = initialQtySelector ? parseInt(initialQtySelector.value) : 1;
    }
    
    // Start watching for Recharge clicks
    watchRechargeClicks();

    // ============================================================================
    // STORE FLAGS
    // ============================================================================
    var storeFlags = window.JD_STORE_FLAGS || {};

    if (storeFlags.forceOtpOnly) {
      // Hide Recharge widget entirely, auto-activate OTP mode
      document.body.classList.add('jd-force-otp-only');
      var rechargeWidget = getRechargeWidget();
      if (rechargeWidget) rechargeWidget.style.display = 'none';
      onetimeRadio.checked = true;
      subscriptionRadio.checked = false;
      updateSections();
    }

    if (storeFlags.soldOutMode) {
      document.body.classList.add('jd-sold-out-mode');
      if (window.theme && window.theme.strings) window.theme.strings.addToCart = 'Sold Out';
      // Delay to run after forceOtpOnly's updateSections() triggers Alpine re-render
      setTimeout(function() {
        var el = document.querySelector('[x-text="addToCartText"]');
        if (el) {
          el.textContent = 'Sold Out';
          var btn = el.closest('button');
          if (btn) btn.disabled = true;
        }
      }, 600);
    }

    // ============================================================================
    // DISCLAIMER CHECKBOX
    // ============================================================================
    const disclaimerCheckbox = document.getElementById('jd-disclaimer-checkbox');
    if (disclaimerCheckbox) {
      function updateDisclaimerState() {
        if (disclaimerCheckbox.checked) {
          document.body.classList.remove('jd-disclaimer-pending');
          disclaimerCheckbox.closest('.jd-disclaimer').classList.remove('jd-disclaimer--error');
        } else {
          document.body.classList.add('jd-disclaimer-pending');
        }
      }
      updateDisclaimerState();
      disclaimerCheckbox.addEventListener('change', updateDisclaimerState);
    }
  }
  
  // ============================================================================
  // ADD TO CART INTERCEPTION
  // ============================================================================
  
  /**
   * Intercepts form submission to handle custom cart logic
   * - Clears cart before adding
   * - Adds correct variant with selling plan
   * - Redirects based on mode (subscription → cart, OTP → checkout)
   */
  document.addEventListener('submit', function(e) {
    const form = e.target;
    if (form.action && form.action.includes('/cart/add')) {
      if ((window.JD_STORE_FLAGS || {}).soldOutMode) { e.preventDefault(); return; }
      e.preventDefault();
      e.stopPropagation();

      // Block submission if disclaimer not accepted
      const disclaimerCheckbox = document.getElementById('jd-disclaimer-checkbox');
      if (disclaimerCheckbox && !disclaimerCheckbox.checked) {
        disclaimerCheckbox.closest('.jd-disclaimer').classList.add('jd-disclaimer--error');
        disclaimerCheckbox.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      // Determine which variant to add
      let variantId;
      if (currentMode === 'onetime' && otpVariantId) {
        variantId = otpVariantId; // Use selected OTP variant
      } else {
        // Try form DOM first, fall back to config
        const variantInput = form.querySelector('[name="id"]');
        variantId = (variantInput && variantInput.value) ||
                    (config.variants && config.variants.defaultSubPack);
      }
      
      if (!variantId) {
        window.JD_DEBUG?.error('No variant ID found');
        return;
      }
      
      // Get selling plan ID if subscription mode
      let sellingPlanId = null;
      if (currentMode === 'subscription') {
        const sellingPlanInput = form.querySelector('input[name="selling_plan"]');
        sellingPlanId = sellingPlanInput ? sellingPlanInput.value : null;
      }
      
      // Clear cart, add item, then redirect
      fetch('/cart/clear.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      })
      .then(function() {
        // Build add to cart payload
        const addData = {
          items: [{
            id: variantId,
            quantity: currentMode === 'onetime' ? otpQuantity : 1 // Use selected quantity for OTP, always 1 for subscription
          }]
        };

        // Add selling plan if subscription mode
        if (sellingPlanId) {
          addData.items[0].selling_plan = sellingPlanId;
        }

        // Build line item properties from metafields
        const properties = {};
        
        if (currentMode === 'subscription') {
          // Note - with placeholder replacement for subscription
          let noteMessage = config.lineItemProperties?.orderTypeSub || '';
          
          // Replace placeholders if subscription mode and plan selected
          if (noteMessage && sellingPlanId) {
            // Find the plan details from config
            const selectedPlan = config.plans?.find(function(plan) {
              return plan.subPlanId === sellingPlanId || plan.trialPlanId === sellingPlanId;
            });
            
            if (selectedPlan) {
              noteMessage = replacePlaceholders(noteMessage, selectedPlan);
            }
          }
          
          if (noteMessage) {
            properties['Note'] = noteMessage;
          }
          
          // Free Trial (default to "with sub" for product page)
          if (config.lineItemProperties?.trialWithSub) {
            properties['Free Trial'] = config.lineItemProperties.trialWithSub;
          }
        } else {
          // OTP mode - no placeholder replacement needed
          if (config.lineItemProperties?.orderTypeOtp) {
            properties['Note'] = config.lineItemProperties.orderTypeOtp;
          }
          // No Free Trial property for OTP
        }
        
        // Add properties if any exist
        if (Object.keys(properties).length > 0) {
          addData.items[0].properties = properties;
        }
        
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(addData)
        }).then(function(response) {
          if (!response.ok) {
            var status = response.status;
            return response.text().then(function(text) {
              var msg = 'Cart add failed (' + status + ')';
              try { msg = JSON.parse(text).description || JSON.parse(text).message || msg; } catch(e) {}
              throw new Error(msg);
            });
          }
        });
      })
      .then(function() {
        // Handle redirect based on mode
        if (currentMode === 'subscription') {
          window.location.href = '/cart';
        } else {
          window.location.href = '/checkout';
        }
      })
      .catch(function(error) {
        window.JD_DEBUG?.error('Error:', error);
        alert('Error updating cart. Please try again.');
      });
    }
  }, true);
  
  // ============================================================================
  // BROWSER BACK BUTTON HANDLING
  // ============================================================================
  
  /**
   * Restores UI state when user navigates back from cart
   * Re-syncs radio buttons and updates UI accordingly
   */
  window.addEventListener('pageshow', function(event) {
    if (event.persisted || performance.getEntriesByType('navigation')[0]?.type === 'back_forward') {
      
      setTimeout(function() {
        const subscriptionRadio = document.getElementById('purchase-subscription');
        const onetimeRadio = document.getElementById('purchase-onetime');

        // Reset all qty selectors to 1
        document.querySelectorAll('.otp-qty-selector').forEach(function(selector) {
          selector.value = '1';
        });
        otpQuantity = 1;

        if (subscriptionRadio && onetimeRadio) {
          // Determine mode from radio button state
          if (subscriptionRadio.checked) {
            currentMode = 'subscription';
          } else if (onetimeRadio.checked) {
            currentMode = 'onetime';
          }

          // Trigger change event to update UI
          const changeEvent = new Event('change', { bubbles: true });
          if (currentMode === 'subscription') {
            subscriptionRadio.dispatchEvent(changeEvent);
          } else {
            onetimeRadio.dispatchEvent(changeEvent);
          }
        }

        // Re-evaluate disclaimer state after browser restores form state
        const disclaimerCb = document.getElementById('jd-disclaimer-checkbox');
        if (disclaimerCb) {
          if (disclaimerCb.checked) {
            document.body.classList.remove('jd-disclaimer-pending');
          } else {
            document.body.classList.add('jd-disclaimer-pending');
          }
        }
      }, 300); // 300ms delay for UI sync
    }
  });

  // ============================================================================
  // START INITIALIZATION
  // ============================================================================

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', loadConfigAndInit);
  } else {
    loadConfigAndInit();
  }

  // ============================================================================
  // DESKTOP THUMBNAIL STRIP NAV BUTTONS
  // ============================================================================
  function initThumbNav() {
    const scrollEl = document.querySelector('.jd-thumb-scroll');
    const prevBtn = document.querySelector('.jd-thumb-btn--prev');
    const nextBtn = document.querySelector('.jd-thumb-btn--next');
    if (!scrollEl || !prevBtn || !nextBtn) return;

    const scrollAmount = 220;

    function updateThumbBtns() {
      const atStart = scrollEl.scrollLeft <= 0;
      const atEnd = scrollEl.scrollLeft + scrollEl.clientWidth >= scrollEl.scrollWidth - 1;
      prevBtn.style.visibility = atStart ? 'hidden' : '';
      nextBtn.style.visibility = atEnd ? 'hidden' : '';
    }

    prevBtn.addEventListener('click', function() {
      scrollEl.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    });
    nextBtn.addEventListener('click', function() {
      scrollEl.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    });
    scrollEl.addEventListener('scroll', updateThumbBtns, { passive: true });
    updateThumbBtns();

    // Keep active thumbnail scrolled into view when it changes
    const observer = new MutationObserver(function() {
      const active = scrollEl.querySelector('.product-thumbnail-list-item--active');
      if (!active) return;
      const thumbLeft = active.offsetLeft;
      const thumbRight = thumbLeft + active.offsetWidth;
      const visibleLeft = scrollEl.scrollLeft;
      const visibleRight = visibleLeft + scrollEl.clientWidth;
      if (thumbLeft < visibleLeft) {
        scrollEl.scrollTo({ left: thumbLeft, behavior: 'smooth' });
      } else if (thumbRight > visibleRight) {
        scrollEl.scrollTo({ left: thumbRight - scrollEl.clientWidth, behavior: 'smooth' });
      }
    });
    observer.observe(scrollEl, { subtree: true, attributes: true, attributeFilter: ['class'] });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initThumbNav);
  } else {
    initThumbNav();
  }

  // ============================================================================
  // MOBILE SPLIDE ↔ ALPINE SYNC
  // Splide moved (swipe/native arrows) → update Alpine currentMediaId → dots follow
  // ============================================================================
  function initMobileSplideSync() {
    const splideEl = document.querySelector('.splide--product');
    if (!splideEl) return;
    if (!window.slideshows || !window.slideshows[splideEl.id]) {
      setTimeout(initMobileSplideSync, 200);
      return;
    }
    const splide = window.slideshows[splideEl.id];
    const alpineRoot = splideEl.closest('[x-data]');
    if (!alpineRoot) return;

    splide.on('moved', function(newIndex) {
      const slideComp = splide.Components.Slides.getAt(newIndex);
      if (!slideComp) return;
      const mediaId = Number(slideComp.slide.dataset.mediaId);
      if (!mediaId) return;
      const data = window.Alpine && window.Alpine.$data ? window.Alpine.$data(alpineRoot) : null;
      if (data && data.currentMediaId !== mediaId) {
        data.currentMediaId = mediaId;
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() { setTimeout(initMobileSplideSync, 500); });
  } else {
    setTimeout(initMobileSplideSync, 500);
  }

  // ============================================================================
  // RECHARGE SHADOW DOM STYLE INJECTION
  // Removes .rc-purchase-option border inside the shadow root so the external
  // subscription group border (below) is the only visible border.
  // Re-injects on shadow DOM re-render via MutationObserver.
  // ============================================================================
  function injectRechargeShadowStyles() {
    const host = document.querySelector('recharge-subscription-widget');
    if (!host || !host.shadowRoot) return;

    const STYLE_ID = 'jd-shadow-overrides';
    if (host.shadowRoot.getElementById(STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = '.rc-purchase-option { border: none !important; }';
    host.shadowRoot.appendChild(style);

    // Re-inject if shadow DOM is re-rendered
    const observer = new MutationObserver(function() {
      if (!host.shadowRoot.getElementById(STYLE_ID)) {
        injectRechargeShadowStyles();
      }
    });
    observer.observe(host.shadowRoot, { childList: true, subtree: false });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() { setTimeout(injectRechargeShadowStyles, 500); });
  } else {
    setTimeout(injectRechargeShadowStyles, 500);
  }

  // ============================================================================
  // SUBSCRIPTION GROUP BORDER OVERLAY
  // Draws a single border around the Recharge widget + delivery pill by
  // absolutely positioning a pointer-events:none overlay div spanning both.
  // Updates on scroll/resize so it tracks the elements.
  // ============================================================================
  function initSubscriptionGroupBorder() {
    const widget = document.querySelector('.shopify-app-block.recharge-subscription-widget');
    const pill = document.querySelector('.plan-delivery-info');
    const onetimeSection = document.querySelector('.onetime-section');
    if (!widget || !pill || !onetimeSection) return;

    var overlay = document.getElementById('jd-sub-group-border');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'jd-sub-group-border';
      overlay.style.cssText = [
        'position: absolute',
        'pointer-events: none',
        'border: 2px solid #00b1eb',
        'border-radius: 12px',
        'box-sizing: border-box',
        'z-index: 1'
      ].join(';');
      document.body.appendChild(overlay);
    }

    function updateOverlay() {
      var isOTPActive = onetimeSection.classList.contains('active');
      var wR = widget.getBoundingClientRect();
      var pR = pill.getBoundingClientRect();
      var scrollY = window.scrollY || window.pageYOffset;
      var scrollX = window.scrollX || window.pageXOffset;
      var T_PAD = window.innerWidth < 750 ? 3 : 8;
      var B_PAD = 8;
      var S_PAD = 6;
      // When OTP active: pill is hidden (pR.bottom=0), so border only wraps widget
      var bottom = pR.bottom > 0 ? pR.bottom : wR.bottom;
      overlay.style.top     = (wR.top  + scrollY - T_PAD) + 'px';
      overlay.style.left    = (wR.left + scrollX - S_PAD) + 'px';
      overlay.style.width   = (wR.width + S_PAD * 2) + 'px';
      overlay.style.height  = (bottom - wR.top + T_PAD + (isOTPActive ? 0 : B_PAD)) + 'px';
      overlay.style.opacity = isOTPActive ? '0.3' : '1';
    }

    updateOverlay();
    window.addEventListener('scroll', updateOverlay, { passive: true });
    window.addEventListener('resize', updateOverlay, { passive: true });

    // Update when mode switches between subscription and OTP
    new MutationObserver(updateOverlay).observe(onetimeSection, { attributes: true, attributeFilter: ['class'] });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() { setTimeout(initSubscriptionGroupBorder, 600); });
  } else {
    setTimeout(initSubscriptionGroupBorder, 600);
  }

})();
